import assign from 'lodash/assign';
import buildingUtils from 'app/shared/utils/buildingUtils';
import {
  get3dTourUrlFlag,
  listingUtils_getIconType,
  listingUtils_isApartmentBldg,
  listingUtils_makeThumbUrl,
} from 'app/shared/utils/listingUtils';
import dateUtils from 'app/shared/utils/dateUtils';
import map from 'lodash/map';

import Address from 'app/shared/models/Address';
import Amenities from 'app/shared/models/Amenities';
import Broker from 'app/shared/models/Broker';
import Geo from 'app/shared/models/Geo';
import noPhoto from 'images/noPhoto.png';
import Photo from 'app/shared/models/Photo';
import RecencyTime from 'app/shared/models/RecencyTime';
import { logError } from '@zg-rentals/log-error';
import type { ListedBy, TotalMonthlyPriceDisplayInfo } from 'app/types/listingDetails.type';
import type { SummaryListingDetails } from 'app/types/summaryListingDetails.type';
import type Coordinates from 'app/shared/models/Coordinates';
import type Filter from 'app/shared/models/Filter';
import type { ListingMinMaxPriceBeds, Model } from 'app/types/commonTypes.type';

export interface SummaryV2Type {
  active: boolean;
  address: Address;
  aliasEncoded: string;
  amenities: Amenities;
  building: boolean;
  created: number;
  displayName: string;
  geo: Geo;
  iconType: string;
  isApartmentBldg: boolean;
  listingMinMaxPriceBeds: ListingMinMaxPriceBeds;
  isBonusListing: boolean;
  isClaimedFreeListing: boolean;
  isPaidAndNotDemoted: boolean;
  isDemoted: boolean;
  isPaidMultifamily: boolean;
  listingType: string;
  maloneLotIdEncoded: string;
  trusted: boolean;
  averageStarLevel: number | undefined;
  photoCount: number;
  previewPhoto: Photo;
  previewPhotoMed: Photo;
  thumbPhoto: Photo;
  medPhotoUrls: Array<string>;
  priority: number;
  score: number;
  propertyType: string;
  recencyTime: RecencyTime;
  unitCount: number;
  searchKeyword: string;
  unit: string;
  updatedTime: RecencyTime;
  uriBuilding: string;
  uriMalone: string;
  urlMaloneUnit: string;
  uriV2: string;
  userItemTypes: Array<string>;
  studentHousing: boolean;
  seniorHousing: boolean;
  militaryHousing: boolean;
  incomeRestricted: boolean;
  hasSpecialOffers: boolean;
  specialOfferLabel: string;
  models: Array<Model>;
  neighborhood: string;
  has3dTour: boolean;
  broker: Broker;
  listedBy: ListedBy | null;
  fullDescription: string;
  rentalApplicationStatus: string;
  modelTypeUnitCount: number;
  newUnitCount: number;
  priceDropCount: number;
  hasTotalMonthlyPrice: boolean;
  totalMonthlyPriceDisplayInfo: TotalMonthlyPriceDisplayInfo | object;
}

function determineListingType(listings: Array<SummaryListingDetails>) {
  if (listings.length === 1) {
    return String(listings[0].listingType) || '';
  }

  let isRoom = true;
  let nonRoomType = '';

  listings.forEach((listing: SummaryListingDetails) => {
    if (String(listing.listingType).toLowerCase() !== 'room') {
      nonRoomType = listing.listingType;
      isRoom = false;
    }
  });

  if (isRoom) {
    return 'room';
  } else {
    return String(nonRoomType).toLowerCase() || '';
  }
}

const SummaryV2 = function (
  this: SummaryV2Type,
  {
    lotIdEncoded = '',
    geo,
    uri = '',
    listings = [],
    neighborhoods = [],
  }: {
    lotIdEncoded?: string;
    geo?: Coordinates;
    uri?: string;
    listings?: Array<SummaryListingDetails>;
    neighborhoods?: Array<string>;
  } = {},
  filter: Filter,
): SummaryV2Type | false {
  try {
    const { isActive, buildingUserItemTypes, buildingActivated, buildingUpdated, listingMinMaxPriceBeds } =
      // buildingUtils has @ts-nocheck, so we need to cast to unknown first
      buildingUtils.processApiDataForSummaryV2(
        listings as unknown as Array<never>,
        filter as unknown as Record<string, unknown>,
      );
    const [listing] = listings;
    const isBuilding = listings.length > 1;
    const hideStreet = listing.address && listing.address.hideStreet;
    const neighborhood = (listing.neighborhoods && listing.neighborhoods[0]) || '';
    this.active = Boolean(isActive);
    this.address = new Address(assign(listing.address, { neighborhood }));
    this.aliasEncoded = String(listing.aliasEncoded || '');
    // Amenities from API doesn't match AmenitiesParams exactly, use type assertion
    this.amenities = new Amenities(listing?.amenities as never);
    this.building = Boolean(isBuilding);
    this.created = Number(buildingActivated); // TO DO: rename to activated.
    this.displayName = String(isBuilding && !hideStreet ? listing.address.street : listing.title);
    this.geo = new Geo(geo);
    this.iconType = String(listingUtils_getIconType(listing.propertyType.toLowerCase()));
    this.isApartmentBldg = Boolean(
      listingUtils_isApartmentBldg(listing.listingType.toLowerCase(), listing.propertyType.toLowerCase()),
    );
    this.listingMinMaxPriceBeds = listingMinMaxPriceBeds;
    this.isBonusListing = Boolean(listing.isBonusListingCustomer);
    this.isClaimedFreeListing = Boolean(listing.isClaimedFreeListing);
    this.isPaidAndNotDemoted = Boolean(listing.isPaidAndNotDemoted);
    this.isDemoted = Boolean(listing.isDemoted);
    this.isPaidMultifamily = Boolean(listing.isPaidMultifamily);

    // for rooms, we only want to put listingType === 'room' if all the listings in the array
    // are rooms.
    this.listingType = determineListingType(listings); // 'rental'
    this.maloneLotIdEncoded = String(lotIdEncoded || '');
    this.trusted = Boolean(listing.trusted);
    this.averageStarLevel = listing.reviewsSummary && Number(listing.reviewsSummary.averageStarLevel);
    this.photoCount = Number(listing.photoCount || 0);
    this.previewPhoto = new Photo({
      url: listing.medPhotoUrl ? listing.medPhotoUrl : noPhoto,
    });
    this.previewPhotoMed = new Photo({
      url: listing.medPhotoUrl ? listing.medPhotoUrl : noPhoto,
    });
    this.thumbPhoto = new Photo({
      url: listing.medPhotoUrl ? listingUtils_makeThumbUrl(listing.medPhotoUrl) : noPhoto,
    });
    this.medPhotoUrls = ([] as Array<string>).concat(listing.medPhotoUrls);
    this.priority = Number(listing.priority || 0);
    this.score = Number(listing.score || 0);
    this.propertyType = String(listing.propertyType.toLowerCase() || ''); // 'house'
    this.recencyTime = new RecencyTime(dateUtils.recencyTime(buildingActivated, new Date()));
    this.unitCount = Number(listings.length);
    this.searchKeyword = isBuilding
      ? String(listings.length + ' units available')
      : String(listing.searchKeyword || ''); // 'Apartments for Rent'
    this.unit = String(listing.unit || ''); // Unit A
    this.updatedTime = new RecencyTime(dateUtils.recencyTime(buildingUpdated, new Date()));
    this.uriBuilding = String(decodeURI(uri));
    this.uriMalone = String(decodeURI(listing.uriMalone || ''));
    this.urlMaloneUnit = String(listing.unit || '')
      .toLowerCase()
      .replace(' ', '-'); // 'unit-a'
    this.uriV2 = String(isBuilding ? decodeURI(uri || '') : decodeURI(listing.uriMalone || ''));
    this.userItemTypes = map(buildingUserItemTypes || [], (userItem) => {
      return String(userItem);
    });
    this.studentHousing = Boolean(listing.studentHousing);
    this.seniorHousing = Boolean(listing.seniorHousing);
    this.militaryHousing = Boolean(listing.militaryHousing);
    this.incomeRestricted = Boolean(listing.incomeRestricted);
    this.hasSpecialOffers = Boolean(listing.hasSpecialOffers);
    this.specialOfferLabel = String(listing.specialOfferLabel || '');
    // TODO: ADD DATA MODEL + TESTS
    this.models = listing.models || [];
    this.neighborhood = String(neighborhoods[0] || '');
    this.has3dTour = get3dTourUrlFlag(listing);
    // Sanitize PII for inactive listings to prevent exposure in preloaded state
    const sanitizedBrokerData = isActive
      ? listing
      : {
          ...listing,
          // Sanitize PII fields for inactive listings
          contactName: null,
          companyPhoneNumber: null,
          companyName: null,
        };

    this.broker = new Broker(sanitizedBrokerData);
    this.listedBy = isActive && listing.listedBy ? listing.listedBy : null;
    this.fullDescription = String(listing.fullDescription || '');
    this.rentalApplicationStatus = String(listing.rentalApplicationStatus || ''); // HPWEB-6082
    this.modelTypeUnitCount = Number(listing.modelTypeUnitCount || 0);
    this.newUnitCount = Number(listing.newUnitCount || 0);
    this.priceDropCount = Number(listing.priceDropCount || 0);
    this.hasTotalMonthlyPrice = Boolean(listing.hasTotalMonthlyPrice);
    this.totalMonthlyPriceDisplayInfo = listing.totalMonthlyPriceDisplayInfo || {};
    return this;
  } catch (err) {
    logError({ error: err, errorType: 'DataModelError', context: 'SummaryV2' });
    return false;
  }
};

export default SummaryV2;
